'use client';

import Image from "next/image";
import Link from "next/link";
import { useMemo } from "react";
import { Mail, Phone, MapPin, ArrowUpRight, ShoppingCart } from "lucide-react";
import Autoplay from "embla-carousel-autoplay";
import { Carousel, CarouselContent, CarouselItem } from "@ui/carousel";
import { socialLinks, aiLogos } from "@data/socialMediaData";
import { platformReviews } from "@data/platformReviewsData";
import { siteConfig } from "@/lib/siteConfig";
import MailtoLink from "@shared/MailtoLink";
import { useLivePaths } from "@/context/LiveNavContext";
import { filterLiveLinks } from "@/lib/live-nav";
import { STORE_VISIBLE } from "@/lib/storeConfig";

const reviewPlugins = [
  Autoplay({ delay: 2000, stopOnInteraction: false, stopOnMouseEnter: true }),
];

const servicesA = [
  { label: "AI Development", href: "/artificial-intelligence-services" },
  { label: "Software Development", href: "/software-development-services" },
  { label: "Custom Software Development", href: "/custom-software-development" },
  { label: "MVP Development", href: "/mvp-development-services" },
  { label: "SaaS Development", href: "/saas-development-services" },
  { label: "Enterprise Software Development", href: "/enterprise-software-development" },
  { label: "Application Development", href: "/application-development-services" },
  { label: "Web Development", href: "/web-development-services" },
];

const servicesB = [
  { label: "Generative AI Development", href: "/generative-ai-development-services" },
  { label: "AI Chatbot Development", href: "/ai-chatbot-development-services" },
  { label: "AI App Development", href: "/ai-app-development-services" },
  { label: "Cross-Platform App Development", href: "/cross-platform-app-development-services" },
  { label: "Swift App Development", href: "/swift-app-development-services" },
  { label: "Hybrid App Development", href: "/hybrid-app-development-services" },
  { label: "API Development & Integration", href: "/api-development-integration-services" },
  { label: "REST API Development", href: "/rest-api-development-services" },
];

const industries = [
  { label: "AI & Automation", href: "/industries/ai-automation" },
  { label: "Healthcare", href: "/industries/healthcare" },
  { label: "E-commerce", href: "/industries/ecommerce" },
  { label: "Finance", href: "/industries/finance" },
  { label: "Real Estate", href: "/industries/real-estate" },
  { label: "Education", href: "/industries/education" },
  { label: "Travel & Tourism", href: "/industries/travel-tourism" },
  { label: "Manufacturing", href: "/industries/manufacturing" },
  { label: "Restaurant", href: "/industries/restaurant" },
];

const hireDevelopers = [
  { label: "Hire Full-Stack Developer", href: "/contact-us" },
  { label: "Hire AI Developer", href: "/contact-us" },
  { label: "Hire App Developer", href: "/contact-us" },
  { label: "Hire JavaScript Developer", href: "/contact-us" },
  { label: "Hire Node.Js Developer", href: "/contact-us" },
  { label: "Hire Laravel Developer", href: "/contact-us" },
  { label: "Hire PHP Developer", href: "/contact-us" },
  { label: "Hire WordPress Developer", href: "/hire-wordpress-developers" },
  { label: "Hire Magento Developer", href: "/contact-us" },
];

function ReviewBadge({ review }) {
  const hasRating = Boolean(review.rating);

  return (
    <a
      href={review.href}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col h-full rounded-xl overflow-hidden border border-slate-200 hover:border-primary bg-white hover:shadow-[0_0_18px_-3px_var(--color-primary)] transition-all duration-200 backdrop-blur-md bg-white/20"
    >
      <div className="px-2 py-2.5 flex items-center gap-2.5">
        <div
          className="relative w-7 h-7 rounded-md flex items-center justify-center text-white font-bold text-xs flex-shrink-0 overflow-hidden"
          style={{ background: review.icon ? '#fff' : review.bg }}
        >
          {review.icon ? (
            <Image
              src={review.icon}
              alt={review.platform}
              fill
              sizes="28px"
              draggable="false"
              className="object-contain p-1"
            />
          ) : (
            review.abbr
          )}
        </div>
        {hasRating ? (
          <div className="min-w-0">
            <div className="flex items-center gap-1">
              <svg width="11" height="11" viewBox="0 0 24 24" fill={review.starColor}>
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              <span className="text-sm font-bold text-slate-800 leading-none text-white">{review.rating} / <span className="text-ink-foreground">5</span></span>
            </div>
            <p className="text-[10px] mt-0.5 font-medium text-white truncate">
              {review.count}
            </p>
          </div>
        ) : (
          <span className="min-w-0 flex-1 truncate text-sm font-bold text-white">{review.platform}</span>
        )}
      </div>
      <div className="px-3 py-1 bg-slate-100 border-t border-slate-200 mt-auto">
        <p className="text-[10px] text-slate-600 text-center">
          {hasRating ? <>By <span className="font-semibold text-slate-600">{review.platform}</span></> : 'View Profile'}
        </p>
      </div>
    </a>
  );
}

function FooterCol({ heading, links }) {
  return (
    <div className="min-w-0">
      <p className="text-sm font-semibold tracking-widest uppercase text-ink-foreground mb-5">
        {heading}
      </p>
      <ul className="flex flex-col gap-2">
        {links.map((l) => (
          <li key={l.label}>
            <Link href={l.href} className="text-sm text-ink-foreground/70 hover:text-white transition-colors">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function GetInTouchBlock({ className = "" }) {
  return (
    <div className={`footer-contact-grid min-w-0 ${className}`}>
      <div className="footer_ai_wrap border-b border-white/10 pb-5 mb-5">
        <p className="text-sm font-semibold tracking-widest uppercase text-ink-foreground mb-4">
          Ask AI about VelanApps
        </p>
        <div className="inline-flex align-center gap-5">
          {aiLogos.map((s) => (
            <a
              key={s.title}
              href={s.href}
              aria-label={s.title}
              title={s.title}
              target="_blank"
              rel="noopener noreferrer"
              className="footer_ai_link w-inline-block backdrop-blur-xl shadow-inner transition-all duration-200"
            >
              <div className="footer_ai_link_icon w-embed">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {s.svg}
                </svg>
              </div>
            </a>
          ))}
        </div>
      </div>
      
      <p className="text-sm font-semibold tracking-widest uppercase text-ink-foreground mb-3">
        Get in Touch
      </p>
      <ul className="flex flex-col gap-2">
        <li className="min-w-0">
          <a
            href={siteConfig.addressUSA.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group/footer-location flex items-start gap-3 min-w-0 text-sm text-ink-foreground/70 hover:text-white transition-colors"
          >
            <MapPin size={15} className="shrink-0 mt-0.5 text-primary" />
            <span className="break-words">
              {siteConfig.addressUSA.street} {siteConfig.addressUSA.cityStateZip}{" "}
              <ArrowUpRight size={13} className="inline-block align-middle opacity-0 transition-all duration-200 group-hover/footer-location:opacity-100 group-hover/footer-location:translate-x-0.5 group-hover/footer-location:-translate-y-0.5" />
            </span>
          </a>
        </li>
        <li className="min-w-0">
          <a href={`tel:${siteConfig.phoneTel}`} className="flex items-center gap-3 min-w-0 text-md text-ink-foreground/70 hover:text-white transition-colors">
            <Phone size={15} className="shrink-0 text-primary" />
            <span suppressHydrationWarning className="break-words">{siteConfig.phone}</span>
          </a>
        </li>
        <li className="min-w-0">
          <MailtoLink email={siteConfig.email} className="flex items-center gap-3 min-w-0 text-md text-ink-foreground/70 hover:text-white transition-colors">
            <Mail size={15} className="shrink-0 text-primary" />
            <span suppressHydrationWarning className="break-all">{siteConfig.email}</span>
          </MailtoLink>
        </li>
        <li className="text-sm font-semibold tracking-widest uppercase text-ink-foreground mt-2">
          Delivery Center
        </li>
        <li className="min-w-0">
          <a
            href={siteConfig.addressIndia.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group/footer-location flex items-start gap-3 min-w-0 text-sm text-ink-foreground/70 hover:text-white transition-colors"
          >
            <MapPin size={15} className="shrink-0 mt-0.5 text-primary" />
            <span className="break-words">
              {siteConfig.addressIndia.lines.map((line, i) => (
                <span key={line}>
                  {line}
                  {i < siteConfig.addressIndia.lines.length - 1 && <br />}
                </span>
              ))}{" "}
              <ArrowUpRight size={13} className="inline-block align-middle opacity-0 transition-all duration-200 group-hover/footer-location:opacity-100 group-hover/footer-location:translate-x-0.5 group-hover/footer-location:-translate-y-0.5" />
            </span>
          </a>
        </li>
      </ul>
    </div>
  );
}

export default function Footer() {
  const livePaths = useLivePaths();
  const liveIndustries = useMemo(() => filterLiveLinks(industries, livePaths), [livePaths]);

  return (
    <footer className="bg-ink text-white">
      <div className="max-w-[1440px] mx-auto px-6 pt-6 md:pt-16 pb-3 md:pb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-[1.3fr_1fr_1fr_0.6fr_0.85fr_1.2fr] gap-10 border-b border-white/10">
        <div className="min-w-0 lg:col-span-1 lg:max-w-[300px]">
          <div className="mb-5">
            <Image
              src="/images/footer-logo.webp"
              alt="VelanApps"
              width={206}
              height={39}
              className="object-contain"
              draggable="false"
            />
          </div>
          <p className="text-sm text-ink-foreground/70 leading-snug mb-4">
            VelanApps is a trusted outsourced software development company delivering custom software, mobile app development, SaaS, and AI solutions for businesses worldwide.
          </p>
          <p className="text-sm font-semibold tracking-widest uppercase text-ink-foreground mb-3">
            Follow Us
          </p>
          <div className="flex gap-2 flex-wrap">
            {socialLinks.map((s) => (
              <a
                key={s.title}
                href={s.href}
                aria-label={s.title}
                title={s.title}
                target="_blank"
                rel="noopener noreferrer"
                className="group/social w-9 h-9 rounded-full bg-white/5 backdrop-blur-xl border saturate-150 border-white/10 shadow-inner flex items-center justify-center hover:bg-red-gradient hover:border-transparent transition-all duration-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox={s.viewBox} fill="#f9f9f9" className="text-primary group-hover/social:text-white transition-colors duration-200">
                  {s.path}
                </svg>
              </a>
            ))}
          </div>

          {/* Get in touch + Ask AI — mobile/tablet only, positioned right below the social links */}
          <GetInTouchBlock className="lg:hidden border-t border-white/10 pt-6 mt-6" />

          <div className="mt-6">
            <Carousel opts={{ loop: true, align: 'start', dragFree: true }} plugins={reviewPlugins}>
              <CarouselContent className="-ml-2">
                {platformReviews.map((r) => (
                  <CarouselItem key={r.platform} className="pl-2 basis-1/2">
                    <ReviewBadge review={r} />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
        </div>

        {/* Col 2 — Services */}
        <FooterCol heading="Services" links={servicesA} />

        {/* Col 3 — Solutions */}
        <FooterCol heading="Solutions" links={servicesB} />

        {/* Col 4 — Industries */}
        <FooterCol heading="Industries" links={liveIndustries} />

        {/* Col 5 — Hire Developers */}
        <FooterCol heading="Hire Developer" links={hireDevelopers} />

        {/* Col 6 — Get in touch (desktop/web view only) */}
        <GetInTouchBlock className="hidden lg:block" />
      </div>

      {/* Bottom bar */}
      <div className="max-w-[1440px] mx-auto px-6 py-5 flex flex-col xl:flex-row items-center justify-between gap-3 text-sm text-ink-foreground/75">
        <p className="order-2 xl:order-1 flex items-center gap-3">
          © {new Date().getFullYear()} VelanApps All Rights Reserved.
        </p>
        <div className="order-1 xl:order-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
          {STORE_VISIBLE && (
            <Link href="/store" className="hover:text-white transition-colors flex flex-wrap items-center justify-center gap-1.5">
              <ShoppingCart className="relative -top-[2px]" size={15} /> Store
            </Link>
          )}
          <Link href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link href="/terms-conditions" className="hover:text-white transition-colors">Terms & Conditions</Link>
        </div>
      </div>
    </footer>
  );
}
